import ResizableImageEditor from '@vromo/dashboard/src/campaigns/ResizableImageEditor';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';
import { useStore } from '@vromo/mobx-react';
import { Widget } from '@vromo/models';
import Button from '@vromo/ui/components/Button';
import Input from '@vromo/ui/components/Input';
import LoadingIndicator from '@vromo/ui/components/LoadingIndicator';
import Select from '@vromo/ui/components/Select';
import { useToasts } from '@vromo/ui/components/Toasts';
import { action, observable, runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { Campaign } from 'models/Campaign';
import React, { ChangeEvent, useRef, useState } from 'react';
import { update } from 'serializr';
import { qaLabelCampaigns } from './../../../../../../../common-qa-labels/campaigns';
import styles from './widgetEditor.style';
import { promotionInputs, ratingInputs, youtubeID } from './widgetUtils';
import WidgetTriggerSelector from './components/WidgetTriggerSelector';
import InputSection from './components/InputSection';

interface Props {
    campaign: Campaign;
    close: () => void;
}

const WidgetEditor = observer<Props>(({ campaign, close }) => {
    const [loading, setLoading] = useState(false);
    const [widget, setWidget] = useState(new Widget());

    const rootStore = useStore<RootStore>('rootStore');

    const file = useRef(null);

    const showToast = useToasts();

    const { graph } = rootStore.authStore;

    const addWidget = async () => {
        setLoading(true);

        if (widget.type === 'video') {
            const id = youtubeID(widget.content);

            if (!id) {
                setLoading(false);
                alert("It looks like the URL you provided wasn't a valid YouTube URL");
                return;
            }

            runInAction(() => (widget.content = id));
        }

        if (widget.type === 'promotion' || widget.type === 'image') {
            if (file?.current?.type) {
                try {
                    const response = await graph.root
                        .graph()
                        .user()
                        .uploads()
                        .post([{ type: file.current.type }]);

                    await fetch(response?.items[0]?.upload, {
                        method: 'PUT',
                        body: file.current
                    });

                    runInAction(() => {
                        widget.content = response.items[0].url;
                    });
                } catch (e) {
                    console.error(e);
                    setLoading(false);
                    showToast('Error uploading image for this widget.', 'error');
                }
            } else {
                setLoading(false);
                showToast(
                    'Please ensure that an image has been uploaded for this widget.',
                    'warning'
                );
            }
        }

        if (widget.type === 'address_correction') {
            widget.settings.popup === true;
        }

        runInAction(() => {
            if (!campaign?.widgets) {
                campaign.widgets = [];
            }

            campaign.widgets.push(toJS(widget));
        });

        setLoading(false);
        setWidget(observable(new Widget()));
        close();
    };

    const ImageSelector = () => (
        <ResizableImageEditor
            aspectRatio={16 / 9}
            maxWidth={1000}
            onChange={action((blob) => (file.current = blob))}
        />
    );

    return (
        <div style={styles.container}>
            <hr />
            <label>Type</label>
            <Select
                data-testid={qaLabelCampaigns.editModal.dropdownwidget}
                value={widget.type}
                onChange={action((e: ChangeEvent<HTMLSelectElement>) => {
                    update(widget, {
                        ...new Widget(),
                        type: e.target.value
                    });
                })}>
                <option value="image">Image</option>
                <option value="video">YouTube Video</option>
                <option value="text">Text</option>
                <option value="promotion">Promotion</option>
                <option value="tip">Driver Tip</option>
                <option value="rating">Rating</option>
                <option value="address_correction">Address Correction</option>
            </Select>

            {widget.type === 'image' && <ImageSelector />}
            {widget.type === 'video' && (
                <Input
                    value={widget.content}
                    onChange={action(
                        (e: ChangeEvent<HTMLInputElement>) => (widget.content = e.target.value)
                    )}
                    placeholder={'e.g. https://youtube.com/embed/123456'}
                />
            )}
            {widget.type === 'text' && (
                <Input
                    data-testid={qaLabelCampaigns.editModal.textWidget.nameInput}
                    value={widget.content}
                    onChange={action(
                        (e: ChangeEvent<HTMLInputElement>) => (widget.content = e.target.value)
                    )}
                    placeholder={'e.g. Your order is on the way'}
                />
            )}
            {widget.type === 'promotion' && (
                <div style={styles.promotionContainer}>
                    <div style={styles.imageSelectContainer}>
                        <ImageSelector />
                    </div>
                    <div style={styles.widgetContainer}>
                        {promotionInputs.map((item) => (
                            <InputSection {...{widget, item}} />
                        ))}
                        <WidgetTriggerSelector widget={widget} />
                    </div>
                </div>
            )}

            {widget.type === 'rating' && (
                <div style={styles.widgetContainer}>
                    {ratingInputs.map((item) => (
                        <InputSection {...{widget, item}} />
                    ))}
                    <WidgetTriggerSelector widget={widget} />
                </div>
            )}
            {widget.type === 'address_correction' && (
                <div style={styles.widgetContainer}>
                    <WidgetTriggerSelector widget={widget} checked disabled />
                </div>
            )}
            {loading ? (
                <LoadingIndicator />
            ) : (
                <div style={styles.buttonContainer}>
                    <Button onClick={close}>Cancel</Button>
                    <Button
                        style={styles.addButton}
                        data-testid={qaLabelCampaigns.editModal.widgedAddBtn}
                        onClick={addWidget}>
                        Add
                    </Button>
                </div>
            )}
            <hr />
        </div>
    );
});

export default WidgetEditor;
